import React from 'react';
import '../../styles/configurator.css';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import RealisticRackVisualization from '../../utils/RealisticRackVisualization.jsx';
import { Toggle } from '../../components/ui/toggle';
import { getCapacityUnit, getThroughputUnit } from '../../utils/configUtilities';
import { resolveFlashNodeModel } from '../../utils/flashNodeModel';
import { MetricValue } from '../../components/ValuePair';
import ExecMetricsView from '../ExecMetricsView';
import { showCustomMetricsPanel } from '../../utils/customMetricsEnabled';

// CONF-212: stable default so a caller that passes nothing (every caller
// until Task 9 wires the exec overrides through) renders exactly as before —
// MetricValue treats an empty set as "nothing is overridden."
const EMPTY_KEYS = new Set();

const ExpansionSystemAttributesCard = ({
    config,
    updateConfig,
    batchUpdateConfig,
    productDb,
    metrics,
    baselineMetrics = metrics,
    affectedKeys = EMPTY_KEYS,
    pricingData,
    hardwareVendor,
    theme,
    consultationMode,
    hasExecAccess = false,
    execMetrics = null,
    showExecMetrics = false,
    onToggleExecMetrics = () => {},
    staleKeys = EMPTY_KEYS,
}) => {
    // CONF-212: execGate also requires execMetrics (the hook result) so the
    // button never renders without something to toggle.
    const execGate = showCustomMetricsPanel({ hasExecAccess, consultationMode }) && execMetrics;
    // CONF-212: these build the exact JSX children the original markup used
    // for each tile (same text runs, same &nbsp; entities, same metric-unit
    // spans — this card's own whitespace quirks, which differ slightly from
    // SystemAttributesCard's), parameterized on which metrics object
    // (baseline or display) to read numbers from. Passing the result
    // straight into MetricValue's `current`/`custom` means the unaffected
    // branch — `<p className="metric-value">{custom}</p>` with `custom`
    // built from `metrics` — renders byte-identical to the pre-CONF-212
    // markup, since it's the same JSX, just moved into a function.
    const rawCapacityNode = (m) => (
        <>
            {m.totalRawCapacity.toLocaleString(undefined, { maximumFractionDigits: 0 })} {getCapacityUnit(config.isPBUnit)} &nbsp;
            <span className="metric-unit">
            ({m.ratioTotalSsdHdd.toLocaleString(undefined, { maximumFractionDigits: 1 })}%,&nbsp;
            {m.totalSsdCapacity.toLocaleString(undefined, { maximumFractionDigits: 0 })} {getCapacityUnit(config.isPBUnit)} NVMe)
            </span>
        </>
    );

    // useableEff has no current/baseline distinction of its own — it is not
    // an override-able figure — so both sides of the pair read it from the
    // live `metrics`, per CONF-212 controller ruling; only the capacity
    // number itself differs between current and custom.
    const useableCapacityNode = (totalEffectiveCapacity) => (
        <>
            {totalEffectiveCapacity.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit">  {getCapacityUnit(config.isPBUnit)}  ({metrics.useableEff.toLocaleString(undefined, { maximumFractionDigits: 0 })}% Util)  </span>
        </>
    );

    const effectiveCapacityNode = (m) => (
        <>
            {m.totalCompressedEffectiveCapacity.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit">  {getCapacityUnit(config.isPBUnit, 'e')} </span>
            {config.hybridEnabled && (
            <>
                / {((config.afaEnabled ? m.afaCompressedEffectiveCapacity : 0) + (config.hybridEnabled ? m.hybridBurstCapacity : 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                <span className="metric-unit">  {getCapacityUnit(config.isPBUnit)} NVMe &nbsp;</span>
            </>
            )}
        </>
    );

    const afaRawCapacityNode = (m) => (
        <>
            {(m.totalAfaRawCapacity || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit"> {getCapacityUnit(config.isPBUnit, 'r')}</span>
        </>
    );

    const afaUseableCapacityNode = (m) => (
        <>
            {(m.afaEffectiveCapacity || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit"> {getCapacityUnit(config.isPBUnit)}</span>
        </>
    );

    const afaEffectiveCapacityNode = (m) => (
        <>
            {(m.afaCompressedEffectiveCapacity || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit"> {getCapacityUnit(config.isPBUnit, 'e')}</span>
        </>
    );

    const hybridRawCapacityNode = (m) => (
        <>
            {(m.totalHybridRawCapacity || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit"> {getCapacityUnit(config.isPBUnit, 'r')}</span>
        </>
    );

    const hybridUseableCapacityNode = (m) => (
        <>
            {(m.hybridEffectiveCapacity || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit"> {getCapacityUnit(config.isPBUnit)}</span>
        </>
    );

    const hybridEffectiveCapacityNode = (m) => (
        <>
            {(m.hybridCompressedEffectiveCapacity || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="metric-unit"> {getCapacityUnit(config.isPBUnit, 'e')}</span>
        </>
    );

    return (
        <>
        <Card>
            <CardHeader className={theme.cardHeader}>
                <div className="flex items-center justify-between">
                    <CardTitle className={theme.cardTitle}>
                        Expansion System Attributes
                        {config.quoteDescription && (
                            <span className="font-bold text-gray-800"> - <span className="underline">{config.quoteDescription}</span></span>
                        )}
                    </CardTitle>
                    <div className="flex items-center gap-4">
                        {execGate && (
                            <button type="button" aria-expanded={showExecMetrics} aria-controls="exec-metrics-panel"
                              onClick={onToggleExecMetrics}
                              className={`flex items-center gap-1 px-3 py-1 rounded border ${showExecMetrics ? theme.button : 'bg-black text-gray-300 hover:bg-gray-800'}`}>
                              Custom Metrics
                              <span className={`text-xs transition-transform ${showExecMetrics ? 'rotate-180' : ''}`}>▾</span>
                            </button>
                        )}
                        <Toggle
                            leftLabel="Decimal (PB)"
                            rightLabel="Binary (PiB)"
                            isRightActive={!config.isPBUnit}
                            onToggle={() => updateConfig({ isPBUnit: !config.isPBUnit })}
                            labelClassName={theme.toggleLabelClassName}
                        />
                    </div>
                </div>
            </CardHeader>

            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6 text-white">
                {execGate && showExecMetrics && (
                    <div className="md:col-span-4">
                        <ExecMetricsView execMetrics={execMetrics} baselineMetrics={baselineMetrics} config={config} staleKeys={staleKeys} />
                    </div>
                )}
                {/* Capacity and Cost Column - Always displayed */}
                <div className="space-y-6">
                    <h3 className="section-header-underlined">Capacity and Cost</h3>
                    <div className="config-column">
                        <div>
                            <p className="metric-label">RAW Capacity ({getCapacityUnit(config.isPBUnit, 'r')})</p>
                            <MetricValue metricKey="totalRawCapacity" affectedKeys={affectedKeys}
                              current={rawCapacityNode(baselineMetrics)}
                              custom={rawCapacityNode(metrics)} />
                        </div>
                        <div>
                            <p className="metric-label">Useable Capacity ({getCapacityUnit(config.isPBUnit)})</p>
                            <MetricValue metricKey="totalEffectiveCapacity" affectedKeys={affectedKeys}
                              current={useableCapacityNode(baselineMetrics.totalEffectiveCapacity)}
                              custom={useableCapacityNode(metrics.totalEffectiveCapacity)} />
                        </div>
                        <div>
                            <p className="metric-label">Effective Capacity ({getCapacityUnit(config.isPBUnit, 'e')})</p>
                            <MetricValue metricKey="totalCompressedEffectiveCapacity" affectedKeys={affectedKeys}
                              current={effectiveCapacityNode(baselineMetrics)}
                              custom={effectiveCapacityNode(metrics)} />
                        </div>

                        {/* MORK TODO: temporarily hidden until engineering provides better details */}
                        <p className="metric-label">iNodes</p>
                        <p className="metric-value">
                            {/* {(metrics.totalMetadata).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                            <span className="metric-unit"> k/s </span>
                            /  */}
                            {(metrics.totalInodes).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            <span className="metric-unit"> M</span>
                        </p>
                    </div>
                    <div>
                        <p className="metric-label">Power (Typical / Max)</p>
                        <p className="metric-value">
                            {(metrics.totalNominalPower / 1000).toFixed(1)}
                            <span className="metric-unit"> kW / </span>
                            {metrics.totalMaxPower > 0
                              ? (metrics.totalMaxPower / 1000).toFixed(1)
                              : 'N/A'}
                            <span className="metric-unit"> kW</span>
                        </p>
                    </div>
                    <div>
                        <p className="metric-label">Weight / Rack Units</p>
                        <p className="metric-value">
                            {metrics.totalWeight.toFixed(0)}
                            <span className="metric-unit"> kg / </span>
                            {metrics.totalRU.toFixed(0)}
                            <span className="metric-unit"> RU</span>
                        </p>
                    </div>
                    {!consultationMode && (
                        <div>
                            <p className="metric-label">Expansion Price</p>
                            <p className="metric-value">
                                ${Number(pricingData?.solution?.grandTotal || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                        </div>
                    )}
                    <div>
                    </div>
                </div>

                {/* AFA Storage Set Column - Only displayed if afaNodeCount > 0 */}
                {config.afaEnabled && (
                    <div className="space-y-6">
                        <h3 className="section-header-underlined">Flash Storage Set ( {config.afaNodeCount} {resolveFlashNodeModel(config).label} )</h3>
                        <div className="config-column">
                            <div>
                                <p className="metric-label">Raw Capacity ({getCapacityUnit(config.isPBUnit, 'r')})</p>
                                <MetricValue metricKey="totalAfaRawCapacity" affectedKeys={affectedKeys}
                                  current={afaRawCapacityNode(baselineMetrics)}
                                  custom={afaRawCapacityNode(metrics)} />
                            </div>
                            <div>
                                <p className="metric-label">Useable Capacity ({getCapacityUnit(config.isPBUnit)})</p>
                                <MetricValue metricKey="afaEffectiveCapacity" affectedKeys={affectedKeys}
                                  current={afaUseableCapacityNode(baselineMetrics)}
                                  custom={afaUseableCapacityNode(metrics)} />
                            </div>
                            <div>
                                <p className="metric-label">Effective Capacity ({getCapacityUnit(config.isPBUnit, 'e')})</p>
                                <MetricValue metricKey="afaCompressedEffectiveCapacity" affectedKeys={affectedKeys}
                                  current={afaEffectiveCapacityNode(baselineMetrics)}
                                  custom={afaEffectiveCapacityNode(metrics)} />
                            </div>
                            {/* MORK TODO : Remove Flash IOPs from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove Flash Throughput from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Hybrid Storage Set Column - Only displayed if hybrid is enabled */}
                {config.hybridEnabled && (
                    <div className="space-y-6">
                        <h3 className="section-header-underlined">Mixed Fleet Storage Set ( {config.hybridNodeCount} S-Nodes )</h3>
                        <div className="config-column">
                            <div>
                                <p className="metric-label">Raw Capacity ({getCapacityUnit(config.isPBUnit, 'r')})</p>
                                <MetricValue metricKey="totalHybridRawCapacity" affectedKeys={affectedKeys}
                                  current={hybridRawCapacityNode(baselineMetrics)}
                                  custom={hybridRawCapacityNode(metrics)} />
                            </div>
                            <div>
                                <p className="metric-label">Useable Capacity ({getCapacityUnit(config.isPBUnit)})</p>
                                <MetricValue metricKey="hybridEffectiveCapacity" affectedKeys={affectedKeys}
                                  current={hybridUseableCapacityNode(baselineMetrics)}
                                  custom={hybridUseableCapacityNode(metrics)} />
                            </div>
                            <div>
                                <p className="metric-label">Effective Capacity ({getCapacityUnit(config.isPBUnit, 'e')})</p>
                                <MetricValue metricKey="hybridCompressedEffectiveCapacity" affectedKeys={affectedKeys}
                                  current={hybridEffectiveCapacityNode(baselineMetrics)}
                                  custom={hybridEffectiveCapacityNode(metrics)} />
                            </div>
                            {/* CONF-94: shown so the customer can see WHY usable capacity
                                and the durability figure differ from the non-MLEC case. */}
                            <div>
                                <p className="metric-label">MLEC</p>
                                <p className="metric-value">{config.hybridMLEC ? 'Enabled' : 'Disabled'}</p>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                            {/* MORK TODO : Remove from display */}
                            <div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Physical Attributes Column */}
                <div className="space-y-6">
                    <h3 className="section-header">System Visualization</h3>
                    <RealisticRackVisualization config={config} metrics={metrics} productDb={productDb} onRackSizeChange={(size) => updateConfig({ rackSize: size })}/>
                    <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                    </div>
                </div>
            </CardContent>
        </Card>
        </>
    );
}

export default ExpansionSystemAttributesCard;